import { useRequest } from "../helpers/useRequest";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { api } from "../config/apiEndpoints";
import { queryEnums } from "../config/queryEnums";
import { useMessage } from "../helpers/useMessages";
import {
  Organisation,
  Office,
  Project,
  DocumentDetails,
  Folder
} from "types";

// Define interfaces for foreign data types
interface ForeignOffice {
    id: number;
    name: string;
    description?: string;
}

interface ForeignSyncResult {
    id: string;
    foreignId: string | number;
    success: boolean;
    error?: string;
}

interface ForeignSyncResponse {
    offices: ForeignSyncResult[];
    projects: ForeignSyncResult[];
    documents: ForeignSyncResult[];
}


// Mapping DTOs
interface OrganizationMappingDTO {
    organizationId: string;
    foreignId: number;
}

interface OfficeMappingDTO {
    officeId: string;
    foreignId: number;
}

interface ProjectMappingDTO {
    projectId: string;
    foreignId: number;
}

interface DocumentMappingDTO {
    documentId: string;
    foreignId: string;
}

interface FolderMappingDTO {
    folderId: string;
    foreignId: number;
}

// Mapping Results
interface OrganizationMappingResult {
    organizationId: string;
    foreignId: number;
    success: boolean;
    message?: string;
}

interface OfficeMappingResult {
    officeId: string;
    foreignId: number;
    success: boolean;
    message?: string;
}

interface ProjectMappingResult {
    projectId: string;
    foreignId: number;
    success: boolean;
    message?: string;
}

interface DocumentMappingResult {
    documentId: string;
    foreignId: string;
    success: boolean;
    message?: string;
}

const url = api.foreign;

// External Office Endpoints
export const useGetExternalOffices = () => {
    const { get } = useRequest();
    return useQuery<ForeignOffice[]>(
        queryEnums.foreign_external_offices,
        () => get(`${url}/offices`)
    );
};

// Organization Endpoints
export const useGetUnmappedOrganizations = () => {
    const { get } = useRequest();
    return useQuery<Organisation[]>(
        queryEnums.foreign_unmapped_organizations,
        () => get(`${url}/organization/unmapped`)
    );
};

export const useGetMappedOrganizations = () => {
    const { get } = useRequest();
    return useQuery<Organisation[]>(
        queryEnums.foreign_mapped_organizations,
        () => get(`${url}/organization/mapped`)
    );
};

export const useGetOrganizationByForeignId = (foreignId?: number) => {
    const { get } = useRequest();
    return useQuery<Organisation>(
        [queryEnums.foreign_organization, foreignId],
        () => get(`${url}/organization/foreign/${foreignId}`),
        { enabled: !!foreignId }
    );
};

export const useSetOrganizationForeignId = () => {
    const { patch } = useRequest();
    const queryClient = useQueryClient();
    const { sendSuccess, sendError } = useMessage();

    return useMutation(
        ({ organizationId, foreignId }: OrganizationMappingDTO) =>
            patch(`${url}/organization/${organizationId}/setForeignId`, foreignId),
        {
            onSuccess: (_result, { organizationId, foreignId }) => {
                sendSuccess(`Organization ${organizationId} mapped to foreign ID ${foreignId}`);
                queryClient.invalidateQueries(queryEnums.foreign_mapped_organizations);
                queryClient.invalidateQueries(queryEnums.foreign_unmapped_organizations);
            },
            onError: (error: any) => {
                sendError(`Failed to set organization foreign ID: ${error.message}`);
            }
        }
    );
};

export const useBulkMapOrganizations = () => {
    const { post } = useRequest();
    const queryClient = useQueryClient();
    const { sendSuccess, sendError } = useMessage();

    return useMutation<OrganizationMappingResult[], Error, OrganizationMappingDTO[]>(
        (mappings: OrganizationMappingDTO[]) =>
            post(`${url}/organization/bulk-map`, mappings),
        {
            onSuccess: (_result) => {
                sendSuccess(`Organizations mapped successfully`);
                queryClient.invalidateQueries(queryEnums.foreign_mapped_organizations);
                queryClient.invalidateQueries(queryEnums.foreign_unmapped_organizations);
            },
            onError: (error: any) => {
                sendError(`Failed to bulk map organizations: ${error.message}`);
            }
        }
    );
};

export const useSyncOrganization = () => {
    const { post } = useRequest();
    const queryClient = useQueryClient();
    const { sendSuccess, sendError } = useMessage();

    return useMutation(
        (foreignId: number) => post(`${url}/organization/${foreignId}`),
        {
            onSuccess: (_result, foreignId) => {
                sendSuccess(`Organization with foreign ID ${foreignId} synced successfully`);
                queryClient.invalidateQueries(queryEnums.foreign_mapped_organizations);
            },
            onError: (error: any) => {
                sendError(`Failed to sync organization: ${error.message}`);
            }
        }
    );
};

// Office Endpoints
export const useGetUnmappedOffices = (organizationId?: string) => {
    const { get } = useRequest();
    return useQuery<Office[]>(
        [queryEnums.foreign_unmapped_offices, organizationId],
        () => organizationId 
            ? get(`${url}/office/unmapped/${organizationId}`)
            : get(`${url}/office/unmapped`),
        { enabled: true }
    );
};

export const useGetMappedOffices = (organizationId?: string) => {
    const { get } = useRequest();
    return useQuery<Office[]>(
        [queryEnums.foreign_mapped_offices, organizationId],
        () => organizationId 
            ? get(`${url}/office/mapped/${organizationId}`)
            : get(`${url}/office/mapped`),
        { enabled: true }
    );
};

export const useGetOfficeByForeignId = (foreignId?: number, organizationId?: string) => {
    const { get } = useRequest();
    return useQuery<Office>(
        [queryEnums.foreign_office, foreignId, organizationId],
        () => organizationId 
            ? get(`${url}/office/foreign/${foreignId}/${organizationId}`)
            : get(`${url}/office/foreign/${foreignId}`),
        { enabled: !!foreignId }
    );
};

export const useSetOfficeForeignId = () => {
    const { patch } = useRequest();
    const queryClient = useQueryClient();
    const { sendSuccess, sendError } = useMessage();

    return useMutation(
        ({ officeId, foreignId }: OfficeMappingDTO) =>
            patch(`${url}/office/${officeId}/setForeignId`, foreignId),
        {
            onSuccess: (_result, { officeId, foreignId }) => {
                sendSuccess(`Office ${officeId} mapped to foreign ID ${foreignId}`);
                queryClient.invalidateQueries(queryEnums.foreign_mapped_offices);
                queryClient.invalidateQueries(queryEnums.foreign_unmapped_offices);
            },
            onError: (error: any) => {
                sendError(`Failed to set office foreign ID: ${error.message}`);
            }
        }
    );
};

export const useRemoveOfficeForeignId = () => {
    const { patch } = useRequest();
    const queryClient = useQueryClient();
    const { sendSuccess, sendError } = useMessage();

    return useMutation(
        (officeId: string) => patch(`${url}/office/${officeId}/removeForeignId`),
        {
            onSuccess: (_result, officeId) => {
                sendSuccess(`Foreign ID removed from office ${officeId}`);
                queryClient.invalidateQueries(queryEnums.foreign_mapped_offices);
                queryClient.invalidateQueries(queryEnums.foreign_unmapped_offices);
            },
            onError: (error: any) => {
                sendError(`Failed to remove office foreign ID: ${error.message}`);
            }
        }
    );
};

export const useBulkMapOffices = () => {
    const { post } = useRequest();
    const queryClient = useQueryClient();
    const { sendSuccess, sendError } = useMessage();

    return useMutation<OfficeMappingResult[], Error, OfficeMappingDTO[]>(
        (mappings: OfficeMappingDTO[]) =>
            post(`${url}/office/bulk-map`, mappings),
        {
            onSuccess: (_result) => {
                sendSuccess(`Offices mapped successfully`);
                queryClient.invalidateQueries(queryEnums.foreign_mapped_offices);
                queryClient.invalidateQueries(queryEnums.foreign_unmapped_offices);
            },
            onError: (error: any) => {
                sendError(`Failed to bulk map offices: ${error.message}`);
            }
        }
    );
};

export const useSyncOffice = () => {
    const { post } = useRequest();
    const queryClient = useQueryClient();
    const { sendSuccess, sendError } = useMessage();

    return useMutation(
        (foreignId: number) => post(`${url}/office/sync/${foreignId}`),
        {
            onSuccess: (_result, foreignId) => {
                sendSuccess(`Office with foreign ID ${foreignId} synced successfully`);
                queryClient.invalidateQueries(queryEnums.foreign_mapped_offices);
            },
            onError: (error: any) => {
                sendError(`Failed to sync office: ${error.message}`);
            }
        }
    );
};

// Project Endpoints
export const useGetUnmappedProjects = (officeId?: string) => {
    const { get } = useRequest();
    return useQuery<Project[]>(
        [queryEnums.foreign_unmapped_projects, officeId],
        () => get(`${url}/project/unmapped/${officeId}`),
        { enabled: !!officeId }
    );
};

export const useGetMappedProjects = (officeId?: string) => {
    const { get } = useRequest();
    return useQuery<Project[]>(
        [queryEnums.foreign_mapped_projects, officeId],
        () => get(`${url}/project/mapped/${officeId}`),
        { enabled: !!officeId }
    );
};

export const useGetProjectByForeignId = (foreignId?: number, officeId?: string) => {
    const { get } = useRequest();
    return useQuery<Project>(
        [queryEnums.foreign_project, foreignId, officeId],
        () => get(`${url}/projects/foreign/${foreignId}/${officeId}`),
        { enabled: !!foreignId && !!officeId }
    );
};

export const useSetProjectForeignId = () => {
    const { patch } = useRequest();
    const queryClient = useQueryClient();
    const { sendSuccess, sendError } = useMessage();

    return useMutation(
        ({ projectId, foreignId }: ProjectMappingDTO) =>
            patch(`${url}/project/${projectId}/setForeignId`, foreignId),
        {
            onSuccess: (_result, { projectId, foreignId }) => {
                sendSuccess(`Project ${projectId} mapped to foreign ID ${foreignId}`);
                queryClient.invalidateQueries(queryEnums.foreign_mapped_projects);
                queryClient.invalidateQueries(queryEnums.foreign_unmapped_projects);
            },
            onError: (error: any) => {
                sendError(`Failed to set project foreign ID: ${error.message}`);
            }
        }
    );
};

export const useRemoveProjectForeignId = () => {
    const { patch } = useRequest();
    const queryClient = useQueryClient();
    const { sendSuccess, sendError } = useMessage();

    return useMutation(
        (projectId: string) => patch(`${url}/project/${projectId}/removeForeignId`),
        {
            onSuccess: (_result, projectId) => {
                sendSuccess(`Foreign ID removed from project ${projectId}`);
                queryClient.invalidateQueries(queryEnums.foreign_mapped_projects);
                queryClient.invalidateQueries(queryEnums.foreign_unmapped_projects);
            },
            onError: (error: any) => {
                sendError(`Failed to remove project foreign ID: ${error.message}`);
            }
        }
    );
};

export const useBulkMapProjects = () => {
    const { post } = useRequest();
    const queryClient = useQueryClient();
    const { sendSuccess, sendError } = useMessage();

    return useMutation<ProjectMappingResult[], Error, ProjectMappingDTO[]>(
        (mappings: ProjectMappingDTO[]) =>
            post(`${url}/projects/bulk-map`, mappings),
        {
            onSuccess: (_result) => {
                sendSuccess(`Projects mapped successfully`);
                queryClient.invalidateQueries(queryEnums.foreign_mapped_projects);
                queryClient.invalidateQueries(queryEnums.foreign_unmapped_projects);
            },
            onError: (error: any) => {
                sendError(`Failed to bulk map projects: ${error.message}`);
            }
        }
    );
};

export const useSyncProject = () => {
    const { post } = useRequest();
    const queryClient = useQueryClient();
    const { sendSuccess, sendError } = useMessage();

    return useMutation(
        (foreignId: number) => post(`${url}/project/sync/${foreignId}`),
        {
            onSuccess: (_result, foreignId) => {
                sendSuccess(`Project with foreign ID ${foreignId} synced successfully`);
                queryClient.invalidateQueries(queryEnums.foreign_mapped_projects);
            },
            onError: (error: any) => {
                sendError(`Failed to sync project: ${error.message}`);
            }
        }
    );
};

// Document Endpoints
export const useGetUnmappedDocuments = (projectId?: string) => {
    const { get } = useRequest();
    return useQuery<DocumentDetails[]>(
        [queryEnums.foreign_unmapped_documents, projectId],
        () => get(`${url}/document/unmapped/${projectId}`),
        { enabled: !!projectId }
    );
};

export const useGetMappedDocuments = (projectId?: string) => {
    const { get } = useRequest();
    return useQuery<DocumentDetails[]>(
        [queryEnums.foreign_mapped_documents, projectId],
        () => get(`${url}/document/mapped/${projectId}`),
        { enabled: !!projectId }
    );
};

export const useGetDocumentByForeignId = (foreignId?: string, projectId?: string) => {
    const { get } = useRequest();
    return useQuery<DocumentDetails>(
        [queryEnums.foreign_document, foreignId, projectId],
        () => get(`${url}/documents/foreign/${foreignId}/${projectId}`),
        { enabled: !!foreignId && !!projectId }
    );
};

export const useSetDocumentForeignId = () => {
    const { patch } = useRequest();
    const queryClient = useQueryClient();
    const { sendSuccess, sendError } = useMessage();

    return useMutation(
        ({ documentId, foreignId }: DocumentMappingDTO) =>
            patch(`${url}/document/${documentId}/setForeignId`, foreignId),
        {
            onSuccess: (_result, { documentId, foreignId }) => {
                sendSuccess(`Document ${documentId} mapped to foreign ID ${foreignId}`);
                queryClient.invalidateQueries(queryEnums.foreign_mapped_documents);
                queryClient.invalidateQueries(queryEnums.foreign_unmapped_documents);
            },
            onError: (error: any) => {
                sendError(`Failed to set document foreign ID: ${error.message}`);
            }
        }
    );
};

export const useRemoveDocumentForeignId = () => {
    const { patch } = useRequest();
    const queryClient = useQueryClient();
    const { sendSuccess, sendError } = useMessage();

    return useMutation(
        (documentId: string) => patch(`${url}/document/removeForeignId/${documentId}`),
        {
            onSuccess: (_result, documentId) => {
                sendSuccess(`Foreign ID removed from document ${documentId}`);
                queryClient.invalidateQueries(queryEnums.foreign_mapped_documents);
                queryClient.invalidateQueries(queryEnums.foreign_unmapped_documents);
            },
            onError: (error: any) => {
                sendError(`Failed to remove document foreign ID: ${error.message}`);
            }
        }
    );
};

export const useBulkMapDocuments = () => {
    const { post } = useRequest();
    const queryClient = useQueryClient();
    const { sendSuccess, sendError } = useMessage();

    return useMutation<DocumentMappingResult[], Error, DocumentMappingDTO[]>(
        (mappings: DocumentMappingDTO[]) =>
            post(`${url}/documents/bulk-map`, mappings),
        {
            onSuccess: (_result) => {
                sendSuccess(`Documents mapped successfully`);
                queryClient.invalidateQueries(queryEnums.foreign_mapped_documents);
                queryClient.invalidateQueries(queryEnums.foreign_unmapped_documents);
            },
            onError: (error: any) => {
                sendError(`Failed to bulk map documents: ${error.message}`);
            }
        }
    );
};

export const useSyncDocument = () => {
    const { post } = useRequest();
    const queryClient = useQueryClient();
    const { sendSuccess, sendError } = useMessage();

    return useMutation(
        (foreignId: string) => post(`${url}/document/sync/${foreignId}`),
        {
            onSuccess: (_result, foreignId) => {
                sendSuccess(`Document with foreign ID ${foreignId} synced successfully`);
                queryClient.invalidateQueries(queryEnums.foreign_mapped_documents);
            },
            onError: (error: any) => {
                sendError(`Failed to sync document: ${error.message}`);
            }
        }
    );
};

// Sync All Endpoint
export const useSyncAll = () => {
    const { post } = useRequest();
    const queryClient = useQueryClient();
    const { sendSuccess, sendError } = useMessage();

    return useMutation<ForeignSyncResponse>(
        () => post(`${url}/sync/all`),
        {
            onSuccess: (result) => {
                const totalSuccess = 
                    result.offices.filter(o => o.success).length +
                    result.projects.filter(p => p.success).length +
                    result.documents.filter(d => d.success).length;
                
                const totalEntities = 
                    result.offices.length +
                    result.projects.length +
                    result.documents.length;

                sendSuccess(`Synced ${totalSuccess} out of ${totalEntities} entities successfully`);
                
                // Invalidate all relevant queries
                queryClient.invalidateQueries(queryEnums.foreign_mapped_organizations);
                queryClient.invalidateQueries(queryEnums.foreign_mapped_offices);
                queryClient.invalidateQueries(queryEnums.foreign_mapped_projects);
                queryClient.invalidateQueries(queryEnums.foreign_mapped_documents);
            },
            onError: (error: any) => {
                sendError(`Failed to sync all entities: ${error.message}`);
            }
        }
    );
};



export const useGetUnmappedFolders = (projectId?: string) => {
    const { get } = useRequest();
    return useQuery<Folder[]>(
        [queryEnums.foreign_unmapped_folders, projectId],
        () => get(`${url}/folder/unmapped/${projectId}`),
        { enabled: !!projectId }
    );
};

export const useGetMappedFolders = (projectId?: string) => {
    const { get } = useRequest();
    return useQuery<Folder[]>(
        [queryEnums.foreign_mapped_folders, projectId],
        () => get(`${url}/folder/mapped/${projectId}`),
        { enabled: !!projectId }
    );
};

export const useSetFolderForeignId = () => {
    const { patch } = useRequest();
    const queryClient = useQueryClient();
    const { sendSuccess, sendError } = useMessage();

    return useMutation(
        ({ folderId, foreignId }: FolderMappingDTO) =>
            patch(`${url}/folder/${folderId}/setForeignId`, foreignId),
        {
            onSuccess: (_result, { folderId, foreignId }) => {
                sendSuccess(`Folder ${folderId} mapped to foreign ID ${foreignId}`);
                queryClient.invalidateQueries(queryEnums.foreign_mapped_folders);
                queryClient.invalidateQueries(queryEnums.foreign_unmapped_folders);
            },
            onError: (error: any) => {
                sendError(`Failed to set folder foreign ID: ${error.message}`);
            }
        }
    );
};

export const useRemoveFolderForeignId = () => {
    const { patch } = useRequest();
    const queryClient = useQueryClient();
    const { sendSuccess, sendError } = useMessage();

    return useMutation(
        (folderId: string) => patch(`${url}/folder/${folderId}/removeForeignId`),
        {
            onSuccess: (_result, folderId) => {
                sendSuccess(`Foreign ID removed from folder ${folderId}`);
                queryClient.invalidateQueries(queryEnums.foreign_mapped_folders);
                queryClient.invalidateQueries(queryEnums.foreign_unmapped_folders);
            },
            onError: (error: any) => {
                sendError(`Failed to remove folder foreign ID: ${error.message}`);
            }
        }
    );
}; 